import { PaymentMethodProps } from './PaymentMethod';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import React, { useMemo } from 'react';
import useAuth from '../../../hooks/global/useAuth';
import { usePaymentCoordinator } from '../../../hooks/payment/usePaymentCoordinator';
import SavePaymentInfoConsent from './SavePaymentInfoConsent';
import AttachablePaymentMethod from './AttachablePaymentMethod';
import PaymentMethodOption from './PaymentMethodOption';
import { PaymentMethod, PaypalVariant } from '../../../services/exports/Constants';

export default function PaypalPaymentMethod(props: PaymentMethodProps) {
  const { paypalVariant } = usePaymentFlow();

  // PayPal-direct is gateway-less and approved via the SDK on every order — it has no
  // saved-account / attachment concept. Render a plain selectable row so we never surface
  // a saved PayPal email + remove button (those belong to PayPal-via-Stripe only). The same goes
  // for PayPal through Spreedly, which has no Stripe Elements to collect an address with either.
  if (paypalVariant === PaypalVariant.Direct || paypalVariant === PaypalVariant.ViaSpreedly) {
    return <PaymentMethodOption {...props} />;
  }

  return <AttachablePaymentMethod {...props} content={<Content />} />;
}

function Content() {
  const { isLoggedIn } = useAuth();
  const { loading } = usePaymentCoordinator();
  const {
    loading: blocked,
    selectedPaymentMethod,
    paypalVariant,
    paymentOptions,
    findSavedPaymentMethod,
  } = usePaymentFlow();

  const savedInfo = useMemo(() => findSavedPaymentMethod(PaymentMethod.Paypal), [paymentOptions]);

  if (selectedPaymentMethod !== PaymentMethod.Paypal || loading || blocked) {
    return null;
  }

  // PayPal-direct: selectable row only — the SDK buttons render in the bottom
  // pay-button slot (PayButton), the same place as the other methods' button.
  if (paypalVariant === PaypalVariant.Direct) {
    return null;
  }

  // PayPal through Stripe asks for no address (PayPal returns the payer's name, email and id itself),
  // so all that is left here is the offer to keep the account.
  if (savedInfo || !isLoggedIn) {
    return null;
  }

  return <SavePaymentInfoConsent className="mt-sm" />;
}
